/**
 * Escapes a single CSV cell value:
 * - Wraps in double quotes if it contains commas, quotes, or newlines
 * - Doubles any internal double-quote characters
 */
const escapeCSVValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str?.includes(',') || str?.includes('"') || str?.includes('\n') || str?.includes('\r')) {
    return `"${str?.replace(/"/g, '""')}"`;
  }
  return str;
};

/**
 * Converts an array of objects to a CSV string.
 * @param {Array<Object>} data - Array of row objects
 * @param {Array<{key: string, label: string}>} columns - Column definitions
 * @returns {string} CSV string
 */
const toCSVString = (data, columns) => {
  const header = columns?.map(col => escapeCSVValue(col?.label))?.join(',');
  const rows = data?.map(row =>
    columns?.map(col => escapeCSVValue(row?.[col?.key]))?.join(',')
  );
  return [header, ...rows]?.join('\n');
};

/**
 * Triggers a browser file download for the given CSV content.
 * @param {string} csvContent - The CSV string
 * @param {string} filename - Desired filename (without extension)
 */
const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link?.setAttribute('download', `${filename}.csv`);
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Main export function — formats data and triggers download.
 * @param {Array<Object>} data - Filtered data rows to export
 * @param {Array<{key: string, label: string}>} columns - Column definitions
 * @param {string} filename - Desired filename (without extension)
 */
export const exportToCSV = (data, columns, filename) => {
  if (!data || data?.length === 0) return;
  const csvContent = toCSVString(data, columns);
  downloadCSV(csvContent, filename);
};

// ─── Pre-defined column sets for each screen ─────────────────────────────────

export const PROJECT_CSV_COLUMNS = [
  { key: 'customerName', label: 'Customer Name' },
  { key: 'address', label: 'Address' },
  { key: 'phase', label: 'Phase' },
  { key: 'status', label: 'Status' },
  { key: 'budget', label: 'Budget' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'aduType', label: 'ADU Type' },
  { key: 'contractorName', label: 'Assigned Contractor' }
];

export const CUSTOMER_CSV_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'aduType', label: 'ADU Type' },
  { key: 'projectStatus', label: 'Status' },
  { key: 'projectName', label: 'Linked Project' }
];

export const CONTRACTOR_CSV_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'company', label: 'Company' },
  { key: 'role', label: 'Trade / Specialty' },
  { key: 'availabilityStatus', label: 'Availability' },
  { key: 'rating', label: 'Rating' },
  { key: 'activeAssignments', label: 'Active Assignments' }
];

export const PERMIT_CSV_COLUMNS = [
  { key: 'applicationNumber', label: 'Permit Number' },
  { key: 'permitType', label: 'Type' },
  { key: 'projectName', label: 'Project' },
  { key: 'status', label: 'Status' },
  { key: 'inspector', label: 'Inspector' },
  { key: 'submissionDate', label: 'Submission Date' },
  { key: 'deadline', label: 'Deadline' }
];
